import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');
const AUTH_STATE_EVENT = 'admin-auth-changed';

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const clearAdminSession = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminData');
  window.dispatchEvent(new Event(AUTH_STATE_EVENT));
};

export const notifyAdminSessionChanged = () => {
  window.dispatchEvent(new Event(AUTH_STATE_EVENT));
};

export const ADMIN_AUTH_EVENT = AUTH_STATE_EVENT;

// Request interceptor to add token to headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Placeholder images for development
const placeholderImages = [
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800&h=600&fit=crop', // Toyota
  'https://images.unsplash.com/photo-1619767886558-efdc259cde1a?w=800&h=600&fit=crop', // Honda
  'https://images.unsplash.com/photo-1600712242805-5f78671b24da?w=800&h=600&fit=crop', // Mercedes
  'https://images.unsplash.com/photo-1617531653332-bd46c24f2068?w=800&h=600&fit=crop', // BMW
  'https://images.unsplash.com/photo-1614162692292-7ac56d7f1ef3?w=800&h=600&fit=crop', // Audi
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&h=600&fit=crop'  // Sports car
];

// Response interceptor to handle errors and add placeholder images in development
api.interceptors.response.use(
  (response) => {
    // Add placeholder images to vehicles if they don't have images (development only)
    if (response.data?.data && Array.isArray(response.data.data)) {
      response.data.data = response.data.data.map((vehicle, index) => {
        if (!vehicle.images || vehicle.images.length === 0) {
          return {
            ...vehicle,
            images: [placeholderImages[index % placeholderImages.length]]
          };
        }
        return vehicle;
      });
    } else if (response.data?.data && typeof response.data.data === 'object') {
      // Single vehicle response
      if (!response.data.data.images || response.data.data.images.length === 0) {
        response.data.data.images = [placeholderImages[0]];
      }
    }
    return response;
  },
  (error) => {
    const requestUrl = error.config?.url || '';
    const hadToken = Boolean(localStorage.getItem('adminToken'));

    if (error.response?.status === 401 && hadToken && !requestUrl.includes('/admin/login')) {
      clearAdminSession();
    }
    return Promise.reject(error);
  }
);

// Admin authentication APIs
export const adminAPI = {
  login: (credentials) => api.post('/admin/login', credentials),
  register: (data) => api.post('/admin/register', data)
};

// Vehicle APIs
export const vehicleAPI = {
  getAll: () => api.get('/vehicles'),
  getById: (id) => api.get(`/vehicles/${id}`),
  getFeatured: () => api.get('/vehicles/featured'),
  create: (formData) => api.post('/vehicles', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/vehicles/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/vehicles/${id}`)
};

// Contact APIs
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getAll: (params) => api.get('/contact', { params }),
  getById: (id) => api.get(`/contact/${id}`),
  updateStatus: (id, status) => api.put(`/contact/${id}/status`, { status }),
  delete: (id) => api.delete(`/contact/${id}`),
  getStats: () => api.get('/contact/stats/summary')
};

export const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${API_ORIGIN}${normalized}`;
};

export default api;
